import { useMemo, useState } from 'react';
import { Gauge, Search, Download, ChevronDown } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import {
  useDomainUsage,
  useUsageByProject,
  useUsageByService,
  useUsageRegions,
  useUsageSummary,
  type DomainUsageRow,
  type ProjectUsageGroup,
  type ServiceUsageGroup,
} from '@/api/manticore';
import {
  useServiceCdnBusiness,
  useServiceCdnBusinessByProject,
  useServiceCdnRegions,
  useServiceCdnSummary,
} from '@/api/manticore/service-cdn';
import { useCdnUmbrellas } from '@/api/manticore/cdn-providers';
import { formatBps, formatBytes } from '../_shared/analytics-format';
import { useDebounce } from '../shared';
import { CdnConsoleBreadcrumbs } from '../cdn/CdnConsoleBreadcrumbs';
import { ExportUsageDialog } from './ExportUsageDialog';
import BusinessUsageShell from './BusinessUsageShell';
import { DateTimeRangePicker } from '@/components/date-time-range-picker/DateTimeRangePicker';

type Unit = 'short' | 'gb';
// 'distribution' swaps the domain table for the billing-basis region split
// (VN / International / CN / TW / TH) — org view only.
type GroupMode = 'flat' | 'project' | 'service' | 'distribution';

function formatVolume(bytes: number, unit: Unit): string {
  return unit === 'gb' ? `${(bytes / 1e9).toFixed(2)} GB` : formatBytes(bytes);
}

/**
 * Exact GB with thousands separators — "1.0 PB" hides the real figure.
 * Rounded to the whole GB (≥ .5 up, < .5 down).
 */
function formatGb(bytes: number): string {
  return `${Math.round(bytes / 1e9).toLocaleString('en-US')} GB`;
}

/** Inline variant for headers: "1.97 PB (1,965,845 GB)". */
function volumeWithGb(bytes: number, unit: Unit): string {
  return unit === 'gb' ? formatVolume(bytes, unit) : `${formatVolume(bytes, unit)} (${formatGb(bytes)})`;
}

// ---- Month options -------------------------------------------------------
interface MonthOpt {
  key: string; // YYYY-MM
  label: string; // "Jun 2026"
}
function buildMonths(): MonthOpt[] {
  const now = new Date();
  const out: MonthOpt[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    out.push({ key, label });
  }
  return out;
}
function monthToRange(key: string): { from: string; to: string } {
  const [y, m] = key.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, 1));
  const nextMonth = new Date(Date.UTC(y, m, 1));
  const now = new Date();
  const to = nextMonth.getTime() > now.getTime() ? now : nextMonth;
  return { from: start.toISOString(), to: to.toISOString() };
}

// ---- Custom day-range selection ------------------------------------------
// The period is either a preset month (the 12-month dropdown) or an absolute
// start–end day window the user picks in the calendar — mirrors the Overview
// page's month-or-custom switcher.
type RangeSel = { kind: 'month'; month: string } | { kind: 'custom'; from: string; to: string };

const shortDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

// ---- CSV -----------------------------------------------------------------
function downloadCsv(rows: DomainUsageRow[], periodSlug: string) {
  const header = 'Domain Name,Volume (bytes),Peak Bandwidth (bps)';
  const body = rows.map((r) => `${r.domain_name},${r.volume_bytes},${r.peak_bandwidth_bps}`).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `domain-volume-usages-${periodSlug}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function usageColumns(unit: Unit): ColumnDef<DomainUsageRow, unknown>[] {
  return [
    {
      id: 'domain_name',
      accessorKey: 'domain_name',
      header: 'Domain',
      cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.domain_name}</span>,
    },
    {
      id: 'volume_bytes',
      accessorKey: 'volume_bytes',
      header: 'Volume',
      // Primary figure only — the exact GB total already shows in each card header.
      cell: ({ row }) => <>{formatVolume(row.original.volume_bytes, unit)}</>,
    },
    {
      id: 'peak_bandwidth_bps',
      accessorKey: 'peak_bandwidth_bps',
      header: 'Peak bandwidth',
      cell: ({ row }) => (
        <span style={{ color: 'var(--vn-fg-muted)' }}>{formatBps(row.original.peak_bandwidth_bps)}</span>
      ),
    },
  ];
}

function ProjectGroupedUsage({
  projects,
  unit,
  search,
}: {
  projects: ProjectUsageGroup[];
  unit: Unit;
  search: string;
}) {
  const cols = useMemo(() => usageColumns(unit), [unit]);
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects
      .map((p) => {
        // Backend may send `domains: null` for a project with no traffic in the
        // period — the typed `DomainUsageRow[]` shape isn't guaranteed at runtime.
        const all = p.domains ?? [];
        const domains = q ? all.filter((d) => d.domain_name.toLowerCase().includes(q)) : all;
        const volume = domains.reduce((a, d) => a + d.volume_bytes, 0);
        return { ...p, domains: [...domains].sort((a, b) => b.volume_bytes - a.volume_bytes), volume_bytes: volume };
      })
      .filter((p) => p.domains.length > 0)
      .sort((a, b) => b.volume_bytes - a.volume_bytes);
  }, [projects, search]);

  if (!groups.length) return <EmptyState icon={<Gauge size={28} />} title="No domains" description="No usage matches your filter." />;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {groups.map((p) => (
        <Card key={p.project_id || 'unassigned'}>
          <CardHeader
            actions={
              <span style={{ fontSize: 13, color: 'var(--vn-fg-muted)' }}>
                Total <strong style={{ color: 'var(--vn-fg-default)' }}>{volumeWithGb(p.volume_bytes, unit)}</strong>
              </span>
            }
          >
            <CardTitle>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                {p.project_name || 'Unassigned'}
                <Badge tone="neutral" appearance="subtle" size="sm">
                  {p.domains.length} domain{p.domains.length === 1 ? '' : 's'}
                </Badge>
              </span>
            </CardTitle>
          </CardHeader>
          <CardBody>
            <DataTable
              columns={cols}
              data={p.domains}
              getRowId={(r) => r.domain_id}
              emptyState={
                <EmptyState
                  size="sm"
                  icon={<Gauge size={28} />}
                  title="No usage"
                  description="No domains recorded traffic for this project."
                />
              }
            />
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

// One service family's card: domains nested under their owning project, so the
// org view reads Org → Service → Project → domains. The project of each domain
// comes from the org-wide by-project result (project_resources mapping) — the
// same attribution billing uses.
function ServiceGroupedUsage({
  services,
  projects,
  unit,
  search,
}: {
  services: ServiceUsageGroup[];
  projects: ProjectUsageGroup[];
  unit: Unit;
  search: string;
}) {
  const cols = useMemo(() => usageColumns(unit), [unit]);

  // domain_id → owning project (from the org-wide project grouping).
  const projectByDomain = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const p of projects) {
      for (const d of p.domains ?? []) {
        if (p.project_id) map.set(d.domain_id, { id: p.project_id, name: p.project_name });
      }
    }
    return map;
  }, [projects]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return services
      .map((s) => {
        const all = s.domains ?? [];
        const domains = q ? all.filter((d) => d.domain_name.toLowerCase().includes(q)) : all;
        // Bucket the service's domains by owning project ('' = unassigned).
        const byProject = new Map<string, { name: string; domains: DomainUsageRow[]; volume: number }>();
        for (const d of domains) {
          const proj = projectByDomain.get(d.domain_id);
          const key = proj?.id ?? '';
          const bucket = byProject.get(key) ?? { name: proj?.name ?? 'Unassigned', domains: [], volume: 0 };
          bucket.domains.push(d);
          bucket.volume += d.volume_bytes;
          byProject.set(key, bucket);
        }
        const projectBuckets = [...byProject.entries()]
          .map(([id, b]) => ({ id, ...b, domains: [...b.domains].sort((a, c) => c.volume_bytes - a.volume_bytes) }))
          // Real projects by volume desc, "Unassigned" pinned last.
          .sort((a, b) => (a.id === '') !== (b.id === '') ? (a.id === '' ? 1 : -1) : b.volume - a.volume);
        const volume = domains.reduce((a, d) => a + d.volume_bytes, 0);
        return { ...s, projectBuckets, volume_bytes: volume, domain_count: domains.length };
      })
      .filter((s) => s.domain_count > 0)
      .sort((a, b) => (a.service_id === '') !== (b.service_id === '') ? (a.service_id === '' ? 1 : -1) : b.volume_bytes - a.volume_bytes);
  }, [services, projectByDomain, search]);

  if (!groups.length)
    return <EmptyState icon={<Gauge size={28} />} title="No domains" description="No usage matches your filter." />;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {groups.map((s) => (
        <Card key={s.service_id || 'unassigned'}>
          <CardHeader
            actions={
              <span style={{ fontSize: 13, color: 'var(--vn-fg-muted)' }}>
                Total <strong style={{ color: 'var(--vn-fg-default)' }}>{volumeWithGb(s.volume_bytes, unit)}</strong>
              </span>
            }
          >
            <CardTitle>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                {s.service_name || 'Unassigned'}
                <Badge tone="neutral" appearance="subtle" size="sm">
                  {s.domain_count} domain{s.domain_count === 1 ? '' : 's'}
                </Badge>
                <Badge tone="neutral" appearance="subtle" size="sm">
                  {s.projectBuckets.length} project{s.projectBuckets.length === 1 ? '' : 's'}
                </Badge>
              </span>
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              {s.projectBuckets.map((p) => (
                <div key={p.id || 'unassigned'}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 'var(--vn-space-2)',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {p.name}
                      <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}>
                        {' '}· {p.domains.length} domain{p.domains.length === 1 ? '' : 's'}
                      </span>
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--vn-fg-muted)' }}>{volumeWithGb(p.volume, unit)}</span>
                  </div>
                  <DataTable columns={cols} data={p.domains} getRowId={(r) => r.domain_id} />
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

export default function BusinessUsagePage() {
  // Optional service filter (replaces the retired per-service usage console):
  // null = org-wide roll-up across every CDN service; a value scopes every fetch
  // to that one orchestrator through the service-scoped endpoints.
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const serviceId = selectedServiceId ?? undefined;
  const isScoped = !!serviceId;

  // Service picker options — the org's CDN orchestrators (canonical umbrellas).
  const { data: umbrellaData } = useCdnUmbrellas();
  const umbrellas = umbrellaData?.umbrellas ?? [];

  const months = useMemo(buildMonths, []);
  const [sel, setSel] = useState<RangeSel>({ kind: 'month', month: months[0].key });
  const [unit, setUnit] = useState<Unit>('short');
  // Page opens org-wide → Org → Services → Projects hierarchy. Scoping to one
  // service (via the filter) drops the flat table since there is no service level.
  const [groupMode, setGroupMode] = useState<GroupMode>('service');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [exportOpen, setExportOpen] = useState(false);

  // Switching the service filter: 'all' clears the scope. "By service" grouping
  // is meaningless once scoped to a single service, so fall back to the flat table.
  const handleServiceChange = (v: string) => {
    const id = v === 'all' ? null : v;
    setSelectedServiceId(id);
    if (id && groupMode === 'service') setGroupMode('flat');
  };

  const range = useMemo(
    () => (sel.kind === 'month' ? monthToRange(sel.month) : { from: sel.from, to: sel.to }),
    [sel],
  );
  // Label for the Section header, export dialog + CSV filename. A month shows its
  // "Jun 2026"; a custom window shows "6 Jun – 30 Jun".
  const periodLabel =
    sel.kind === 'month'
      ? months.find((m) => m.key === sel.month)?.label ?? sel.month
      : `${shortDate(range.from)} – ${shortDate(range.to)}`;
  const periodSlug = sel.kind === 'month' ? sel.month : `${range.from.slice(0, 10)}_${range.to.slice(0, 10)}`;

  // Org-wide fetches (disabled when scoped to a service). The by-project result
  // also feeds the service mode: it supplies the domain → project attribution
  // the nested Service → Project grouping needs.
  // Flat per-domain list — used by "Flat" and also by "By distribution" (its
  // table below the region split shows the same per-domain rows).
  const orgFlatQ = useDomainUsage(
    !isScoped && (groupMode === 'flat' || groupMode === 'distribution') ? range : null,
  );
  const orgProjQ = useUsageByProject(
    !isScoped && (groupMode === 'project' || groupMode === 'service') ? range : null,
  );
  const orgSvcQ = useUsageByService(!isScoped && groupMode === 'service' ? range : null);
  // Billing-basis region split (VN incl. untagged / CN / TW / TH / OTHER) — fetched
  // for the "By distribution" view. Org and service-scoped share the same shape and
  // basis (partition of delivered bandwidth); pick by scope.
  const orgRegionsQ = useUsageRegions(!isScoped && groupMode === 'distribution' ? range : null);
  const svcRegionsQ = useServiceCdnRegions(
    serviceId ?? '',
    isScoped && groupMode === 'distribution' ? range : null,
  );
  const regionsQ = isScoped ? svcRegionsQ : orgRegionsQ;
  // Headline KPIs for the strip: total volume, peak bandwidth, peak RPS, domains.
  const orgSummaryQ = useUsageSummary(!isScoped ? range : null);
  const svcSummaryQ = useServiceCdnSummary(serviceId ?? '', isScoped ? range : null);
  const summaryQ = isScoped ? svcSummaryQ : orgSummaryQ;

  // Service-scoped fetches — flat (DomainUsageRow[]) and by-project
  // (ProjectUsageGroup[], incl. an "Unassigned" group) for that service's domains.
  // Flat also backs "By distribution" (its per-domain table under the region split).
  const svcQ = useServiceCdnBusiness(
    serviceId ?? '',
    isScoped && (groupMode === 'flat' || groupMode === 'distribution')
      ? { from: range.from, to: range.to, granularity: 'day' }
      : null,
  );
  const svcProjQ = useServiceCdnBusinessByProject(
    serviceId ?? '',
    isScoped && groupMode === 'project' ? { from: range.from, to: range.to, granularity: 'day' } : null,
  );

  // Resolve which data to show.
  const flatQ = isScoped ? svcQ : orgFlatQ;
  const projQ = isScoped ? svcProjQ : orgProjQ;

  const cols = useMemo(() => usageColumns(unit), [unit]);
  const flatRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? (flatQ.data ?? []).filter((d) => d.domain_name.toLowerCase().includes(q))
      : flatQ.data ?? [];
    return [...rows].sort((a, b) => b.volume_bytes - a.volume_bytes);
  }, [flatQ.data, search]);

  const loading =
    groupMode === 'distribution'
      ? regionsQ.isLoading || flatQ.isLoading
      : groupMode === 'flat'
        ? flatQ.isLoading
        : groupMode === 'service'
          ? orgSvcQ.isLoading || orgProjQ.isLoading
          : projQ.isLoading;
  const csvRows =
    groupMode === 'flat'
      ? flatRows
      : groupMode === 'service'
        ? (orgSvcQ.data ?? []).flatMap((s) => s.domains ?? [])
        : groupMode === 'project'
          ? (projQ.data ?? []).flatMap((p) => p.domains ?? [])
          : flatRows; // distribution: export the flat per-domain list shown under the region split

  // Headline KPI strip (org view): the main metrics — total volume, peak
  // bandwidth, peak request rate, and how many domains carried traffic.
  const mainKpis = useMemo(() => {
    const s = summaryQ.data;
    if (!s) return [];
    return [
      { label: 'Total volume', value: formatVolume(s.volume_bytes, unit), hint: formatGb(s.volume_bytes) },
      { label: 'Peak bandwidth', value: formatBps(s.peak_bandwidth_bps) },
      { label: 'Peak RPS', value: `${Math.round(s.peak_rps).toLocaleString('en-US')} req/s` },
      { label: 'Domains', value: s.domain_count.toLocaleString('en-US') },
    ];
  }, [summaryQ.data, unit]);

  // Billing-basis region split — shown under "By distribution". Total = sum of the
  // region buckets (= delivered volume); VN already contains the untagged remainder
  // billing attributes domestically.
  const regionKpis = useMemo(() => {
    const r = regionsQ.data?.regions;
    if (!r) return [];
    const vn = r.VN ?? 0;
    const cn = r.CN ?? 0;
    const tw = r.TW ?? 0;
    const th = r.TH ?? 0;
    const other = r.OTHER ?? 0;
    const total = vn + cn + tw + th + other;
    // hint always carries the exact GB — "2.0 PB" alone hides the real figure.
    const items = [
      { label: 'Total volume', value: formatVolume(total, unit), hint: formatGb(total) },
      { label: 'Domestic (VN)', value: formatVolume(vn, unit), hint: `${formatGb(vn)} · incl. untagged traffic` },
      { label: 'International', value: formatVolume(other, unit), hint: formatGb(other) },
    ];
    for (const [label, v] of [
      ['China', cn],
      ['Taiwan', tw],
      ['Thailand', th],
    ] as const) {
      if (v > 0) items.push({ label, value: formatVolume(v, unit), hint: formatGb(v) });
    }
    return items;
  }, [regionsQ.data, unit]);

  return (
    <>
      <BusinessUsageShell
        icon={<Gauge size={22} />}
        breadcrumbs={<CdnConsoleBreadcrumbs trail={[{ label: 'Business Usage' }]} />}
        description="Per-domain egress volume and peak bandwidth, aggregated across all CDN vendors."
        basis={
          <>
            Usage is aggregated from the central datasource. Volume sums edge egress across every CDN binding; peak
            bandwidth is the highest simultaneous cross-vendor rate in the selected period.
            {!isScoped && ' The regional split follows the same basis your monthly statement reconciles against.'}
          </>
        }
        period={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Service filter — 'all' rolls up every CDN orchestrator; picking one
                scopes the whole page to that service (replaces the old per-service
                usage console). */}
            <Select value={selectedServiceId ?? 'all'} onValueChange={handleServiceChange}>
              <SelectTrigger size="md" style={{ width: 220, flexShrink: 0 }}>
                <SelectValue placeholder="All services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                {umbrellas.map((u) => (
                  <SelectItem key={u.service_id} value={u.service_id}>
                    {u.service_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={sel.kind === 'month' ? sel.month : ''}
              onValueChange={(v) => setSel({ kind: 'month', month: v })}
            >
              <SelectTrigger size="md" style={{ width: 170, flexShrink: 0 }}>
                <SelectValue placeholder="Custom range" />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Month-or-custom: the calendar + per-endpoint time inputs pick an
                absolute start–end window; applying it deselects the month. The end
                time defaults to 23:59, so picking an end DAY counts through the whole
                of that day — the same inclusive-last-day basis the monthly statement
                reconciles against (end is the exclusive upper bound at query time). */}
            <DateTimeRangePicker
              value={sel.kind === 'custom' ? { from: sel.from, to: sel.to } : { from: '', to: '' }}
              onChange={(v) => {
                if (v.from && v.to) setSel({ kind: 'custom', from: v.from, to: v.to });
              }}
              placeholder="Custom range"
            />
          </div>
        }
        actions={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <SegmentedControl
              aria-label="Grouping"
              size="md"
              items={[
                // "By service" only exists org-wide (a single service has no service
                // level); the service console starts flat. "By distribution" now works
                // in both scopes (service-scoped region split reconciles to the same
                // delivered-bandwidth basis).
                ...(!isScoped ? [{ value: 'service', label: 'By service' }] : []),
                { value: 'flat', label: 'Flat' },
                { value: 'project', label: 'By project' },
                { value: 'distribution', label: 'By distribution' },
              ]}
              value={groupMode}
              onValueChange={(v) => setGroupMode(v as GroupMode)}
            />
            <SegmentedControl
              aria-label="Unit"
              size="md"
              items={[
                { value: 'short', label: 'Short' },
                { value: 'gb', label: 'GB' },
              ]}
              value={unit}
              onValueChange={(v) => setUnit(v as Unit)}
            />
            {/* One export entry point: quick CSV of the rows on screen, or the
                server-generated multi-sheet Excel report via the dialog. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="md" startIcon={<Download size={14} />}>
                  Export
                  <ChevronDown size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={csvRows.length === 0} onSelect={() => downloadCsv(csvRows, periodSlug)}>
                  Download CSV (current view)
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setExportOpen(true)}>
                  Excel report…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
        // Stats strip: summary KPIs, or (under "By distribution") the billing-basis
        // region split in the same slot. Empty array → shell renders no strip.
        kpis={groupMode === 'distribution' ? regionKpis : mainKpis}
        kpisLoading={groupMode === 'distribution' ? undefined : summaryQ.isLoading}
        periodLabel={periodLabel}
        search={
          <Input
            inputSize="md"
            placeholder="Filter domains"
            startSlot={<Search />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 260, flexShrink: 0 }}
          />
        }
      >
        {loading ? (
          <Skeleton style={{ height: 280 }} />
        ) : groupMode === 'distribution' || groupMode === 'flat' ? (
          <DataTable
            columns={cols}
            data={flatRows}
            getRowId={(r) => r.domain_id}
            emptyState={
              <EmptyState
                icon={<Gauge size={28} />}
                title="No usage"
                description="No domains recorded traffic in this period."
              />
            }
          />
        ) : groupMode === 'service' ? (
          <ServiceGroupedUsage
            services={orgSvcQ.data ?? []}
            projects={orgProjQ.data ?? []}
            unit={unit}
            search={search}
          />
        ) : (
          <ProjectGroupedUsage projects={projQ.data ?? []} unit={unit} search={search} />
        )}
      </BusinessUsageShell>

      <ExportUsageDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        serviceId={serviceId}
        monthLabel={periodLabel}
        monthRange={range}
      />
    </>
  );
}
